import React, { useState, useEffect, useCallback } from 'react';
import { notificationSettingsService } from '../../services';
import { Card, Button, Toast } from '../ds';

const FONT = "'IBM Plex Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

const inputStyle = {
  width: '100%', padding: '9px 12px', boxSizing: 'border-box',
  border: '1px solid #D7DCE4', borderRadius: 8, fontSize: 13.5,
  fontFamily: FONT, outline: 'none', color: '#101623',
};
const labelStyle = { display: 'block', fontSize: 12.5, fontWeight: 600, color: '#475569', marginBottom: 6 };

// Feature-Flag (Build-Arg via Host-.env SHOW_SEND_TEST_MAIL, Default aus):
// nur bei explizitem "true" wird der „Test-E-Mail senden"-Button angezeigt.
const SHOW_SEND_TEST_MAIL = process.env.REACT_APP_SHOW_SEND_TEST_MAIL === 'true';

const FREQUENCIES = [
  { value: 'aus', label: 'Aus' },
  { value: 'taeglich', label: 'Täglich' },
  { value: 'woechentlich', label: 'Wöchentlich' },
];

const BenachrichtigungenTab = () => {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await notificationSettingsService.getMySettings();
      setSettings(res.data);
      setForm({
        emailAktiv: res.data.emailAktiv,
        offeneKontrollenFrequenz: res.data.offeneKontrollenFrequenz,
      });
    } catch (err) {
      setError('Benachrichtigungseinstellungen konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <Card padding={22}>
      <div style={{ color: '#8A93A3', fontSize: 13, fontFamily: FONT }}>Lädt…</div>
    </Card>
  );

  if (error) return (
    <Card padding={22}>
      <p style={{ margin: '0 0 14px', fontSize: 13, color: '#C22B31' }}>{error}</p>
      <Button size="md" onClick={load}>Erneut versuchen</Button>
    </Card>
  );

  const initial = {
    emailAktiv: settings.emailAktiv,
    offeneKontrollenFrequenz: settings.offeneKontrollenFrequenz,
  };
  const dirty = Object.keys(initial).some(k => form[k] !== initial[k]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await notificationSettingsService.updateMySettings(form);
      setSettings(res.data);
      setToast({ tone: 'secure', text: 'Benachrichtigungseinstellungen gespeichert.' });
    } catch (err) {
      setToast({ tone: 'critical', text: 'Speichern fehlgeschlagen. Bitte erneut versuchen.' });
    } finally {
      setSaving(false);
    }
  };

  const disabled = !form.emailAktiv;

  const handleSendTest = async () => {
    setSendingTest(true);
    try {
      await notificationSettingsService.sendTestEmail();
      setToast({ tone: 'secure', text: 'Test-E-Mail wurde versendet.' });
    } catch (err) {
      setToast({ tone: 'critical', text: err.response?.data?.error || 'Test-E-Mail konnte nicht gesendet werden.' });
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <Card title="E-Mail-Benachrichtigungen" subtitle="Erinnerungen an offene Kontrollen abgeschlossener Audits" padding={22}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={form.emailAktiv}
          onChange={e => setForm(prev => ({ ...prev, emailAktiv: e.target.checked }))}
          style={{ width: 16, height: 16 }}
        />
        <span style={{ fontSize: 14, fontWeight: 600, color: '#101623' }}>E-Mail-Benachrichtigungen aktivieren</span>
      </label>

      <div style={{ maxWidth: 280, opacity: disabled ? 0.5 : 1 }}>
        <label style={labelStyle}>Erinnerung an offene Kontrollen</label>
        <select
          value={form.offeneKontrollenFrequenz}
          disabled={disabled}
          onChange={e => setForm(prev => ({ ...prev, offeneKontrollenFrequenz: e.target.value }))}
          style={inputStyle}
        >
          {FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
      </div>
      <p style={{ fontSize: 12, color: '#8A93A3', margin: '10px 0 0' }}>
        Benachrichtigungen gehen an die Login-E-Mail-Adresse Ihres Kontos. Mails zu Ihrem Konto
        und Ihren Berichten (z. B. der Sicherheitsbericht nach einem Audit) erhalten Sie unabhängig
        von dieser Einstellung.
      </p>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 20 }}>
        <Button size="md" disabled={!dirty || saving} onClick={handleSave}>{saving ? 'Speichert…' : 'Speichern'}</Button>
        <Button variant="secondary" size="md" disabled={!dirty || saving} onClick={() => setForm(initial)}>Verwerfen</Button>
        {SHOW_SEND_TEST_MAIL && (
          <div style={{ marginLeft: 'auto' }}>
            <Button variant="secondary" size="sm" disabled={sendingTest} onClick={handleSendTest}>
              {sendingTest ? 'Sendet…' : 'Test-E-Mail senden'}
            </Button>
          </div>
        )}
      </div>

      <Toast message={toast?.text} tone={toast?.tone} onDismiss={() => setToast(null)} />
    </Card>
  );
};

export default BenachrichtigungenTab;
